import ActiveToggleButton from "@/components/ActiveToggleButton";
import BaseTable from "@/components/BaseTable/BaseTable";
import { default as CustomButton } from "@/components/common/Button";
import { convertSchemasResponse } from "@/lib/convertSchemasResponse";
import { SchemaService } from "@/services/APIs";
import { gridName } from "@/utils/constant/gridName";
import { tableName } from "@/utils/constant/tableName";
import navigationRoutes from "@/utils/navigationRoutes";
import { TenantSchema } from "@/utils/types/schemas";
import { ProColumns } from "@ant-design/pro-components";
import { useQuery } from "@tanstack/react-query";
import { message } from "antd";
import { t } from "i18next";
// import { t } from "i18next";
import { Edit2, Eye, Plus, RefreshCw } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

const SchemasListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlPage = parseInt(searchParams.get("page") ?? "1", 10);
  const urlPageSize = parseInt(searchParams.get("pageSize") ?? "10", 10);
  const urlQuery = searchParams.get("q") ?? "";
  const [page, setPage] = useState(urlPage);
  const [pageSize, setPageSize] = useState(urlPageSize);
  const [query, setQuery] = useState(urlQuery); // GLOBAL SEARCH TEXT
  const {
    data: schemas,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["schemas", page, pageSize, query],
    queryFn: () =>
      SchemaService.getAllSchemas({ page, pageSize, search: query }),
  });

  const schemaList = useMemo(() => {
    if (!schemas) {
      return [];
    }
    return convertSchemasResponse(schemas.results);
  }, [schemas]);

  const handleEditSchema = (record: TenantSchema) => {
    if (record.is_system) {
      message.error("You cannot edit a system schema");
    } else {
      navigate(navigationRoutes.SCHEMA_EDIT(record.name), {
        state: { schemaName: record.name },
      });
    }
  };

  const handleViewSchema = (record: TenantSchema) => {
    if (record.is_system) {
      message.error("You cannot view a system schema");
    } else {
      navigate(navigationRoutes.SCHEMA_RENDER(record.name));
    }
  };

  const handeleSchemaNav = (record: TenantSchema) => {
    if (record.is_system) {
      message.error("You cannot navigate to a system schema");
    } else if (record.is_child) {
      message.error("You cannot navigate to a child schema");
    } else {
      navigate(navigationRoutes.MODULE(record.name));
    }
  };

  const columns: ProColumns<TenantSchema>[] = [
    {
      title: "Schema Name",
      dataIndex: "name",
      render: (_, record) => (
        <button
          className="font-medium text-primary hover:underline"
          onClick={() => handeleSchemaNav(record)}
        >
          {record.name}
        </button>
      ),
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Is Active",
      width: 120,
      render: (record: any) => {
        return (
          <ActiveToggleButton
            table={tableName.schema}
            pk={record.id}
            is_active={record.is_active}
            disabled={record.is_system}
          />
        );
      },
    },
    {
      title: "Actions",
      width: 140,
      valueType: "option",
      render: (_, record) => {
        if (record.is_system) {
          return <span className="italic text-gray-400">System</span>;
        }

        const actions = [
          <CustomButton
            key="edit"
            onClick={() => handleEditSchema(record)}
            icon={<Edit2 size={16} />}
            type="text"
            title="Edit Schema"
            className="!p-1 hover:!bg-muted"
          />,
        ];

        if (!record.is_child) {
          actions.push(
            <CustomButton
              key="view"
              onClick={() => handleViewSchema(record)}
              icon={<Eye size={16} />}
              type="text"
              title="View Schema"
              className="!p-1 hover:!bg-muted"
            />
          );
        }

        return actions;
      },
    },
  ];

  const toolBarRender = () => {
    return [
      <button
        key="refresh"
        onClick={() => refetch()}
        className="rounded-lg border border-border p-2 transition-colors hover:bg-muted"
        title="Refresh"
      >
        <RefreshCw size={16} className="text-muted-foreground" />
      </button>,
      <CustomButton
        key="add"
        type="primary"
        onClick={() => navigate(navigationRoutes.SCHEMA_ADD)}
        title="Add Schema"
        className="!rounded-lg !px-4 !py-2 !text-sm !font-medium"
      >
        <Plus size={16} className="mr-1" /> {t("Add Schema")}
      </CustomButton>,
    ];
  };

  return (
    <div className="schema-list-page">
      <BaseTable<TenantSchema>
        loading={isLoading}
        columns={columns}
        dataSource={(schemaList as TenantSchema[]) ?? []}
        page={page}
        tableName={gridName.schemaTable}
        pageSize={pageSize}
        total={schemas?.count ?? 0}
        onPageChange={(page, size) => {
          setPage(page);
          setPageSize(size ?? 10);
          searchParams.set("page", page.toString());
          searchParams.set("pageSize", size?.toString() ?? "10");
          navigate(`${navigationRoutes.SCHEMAS}?${searchParams.toString()}`);
        }}
        rowKey="id"
        search={false}
        manualSearch
        searchText={query}
        onSearchChange={val => {
          setPage(1);
          setQuery(val);
        }}
        onSearchSubmit={val => {
          setPage(1);
          setQuery(val);
          searchParams.set("page", "1");
          searchParams.set("pageSize", pageSize.toString());
          searchParams.set("q", val);
          navigate(`${navigationRoutes.SCHEMAS}?${searchParams.toString()}`);
        }}
        searchPlaceholder="Search schemas"
        toolBarRender={toolBarRender}
        headerTitle={t("Schema")}
        request={() => refetch()}
      />
    </div>
  );
};

export default SchemasListPage;
